import { Injectable } from '@angular/core';
import { 
	AddressModel,
	AgentInvitationRequestModel,
	CardPaymentRequestModel,
	ConfigurationModel,
	CountryModel, 
	DataClient, 
	DocumentsClient, 
	EmailClient, 
	FileResponse, 
	GeographicalRegionModel, 
	GetQuoteRatesRequestModel, 
	GetQuoteResponseModel, 
	IGetQuoteRatesRequestModel, 
	MemberModel, 
	MetadataModel, 
	PaymentClient, 
	PaymentProcessorResponseModel, 
	PhoneModel, 
	QuoteClient, 
	QuoteModel, 
	QuoteRateView, 
	SendQuoteByEmailModel
} from './web-api-client';
import { tap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class QuoteService {

	public quoteModel: GetQuoteResponseModel= new GetQuoteResponseModel();
	public quoteRateModel: IGetQuoteRatesRequestModel= new QuoteRateView();
	public fromStepTwo: boolean = false;
	// Precios de los 3 planes (los llena la pantalla de planes) para el PDF propio
	// Los 3 planes se conservan en sessionStorage para que el envío del PDF propio
	// funcione también en los pasos 6/7 aunque se recargue la página.
	private _allPlans: any = null;
	get allPlans(): any {
		if (!this._allPlans) { try { const s = sessionStorage.getItem('ba_allPlans'); if (s) { this._allPlans = JSON.parse(s); } } catch (e) { /* sin storage */ } }
		return this._allPlans;
	}
	set allPlans(v: any) {
		this._allPlans = v;
		try { if (v) { sessionStorage.setItem('ba_allPlans', JSON.stringify(v)); } else { sessionStorage.removeItem('ba_allPlans'); } } catch (e) { /* sin storage */ }
	}
	
	// Custom property to store last error information
	private _lastError: any = null;

	constructor(
		private quoteClient: QuoteClient, 
		private dataClient: DataClient,
		private paymentClient: PaymentClient,
		private documentsClient: DocumentsClient,
		private emailClient: EmailClient,
		private http: HttpClient) {	
	}

	/**
	 * Set the last error information for cross-component communication
	 */
	setLastError(error: any): void {
		this._lastError = error;
	}

	/**
	 * Get the last error information
	 */
	getLastError(): any {
		return this._lastError;
	}

	/**
	 * Clear the last error information
	 */
	clearLastError(): void {
		this._lastError = null;
	}

	createQuote$(data?: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.createQuote(data)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}
	
	getQuote$(id: string): Observable<QuoteModel> {
		
		return this.quoteClient.getQuote(id)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	getConfig$(): Observable<ConfigurationModel> {
		
		return this.quoteClient.getConfig()
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	getRates$(model: GetQuoteRatesRequestModel): Observable<GetQuoteResponseModel> {
		
		return this.quoteClient.getRates(model)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	putRates$(model: GetQuoteRatesRequestModel): Observable<GetQuoteResponseModel> {
		
		return this.quoteClient.putRates(model)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	updateQuote$(data?: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.updateQuote(data)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	} 

	createcertificate$(data?: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.createcertificate(data)
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	createPendingActivationCertificate$(policyQuoteNumber: string): Observable<QuoteModel> {
		return this.quoteClient.createPendingActivationCertificate(policyQuoteNumber)
		.pipe(
			tap(r => {
				// Ensure certificateNumber is stored in the shared quoteModel
				if (r && (r as any).certificateNumber) {
					(this.quoteModel as any).certificateNumber = (r as any).certificateNumber;
				}
			})
		);
	} 	

	getCountries$(): Observable<CountryModel[]> {
		
		return this.dataClient.getCountries()
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	getRegions$(): Observable<GeographicalRegionModel[]> {
		
		return this.dataClient.getRegions()
		.pipe(
			//take(1),
			tap(r => {
			})
		);
	}

	ensureDate(date: any): Date {
		if (!(date instanceof Date)) {
		  date = new Date(date);
		}
		return date;
	}

	makeAPayment$(request: CardPaymentRequestModel): Observable<PaymentProcessorResponseModel> {
		return this.paymentClient.makeACardPayment(request);
	}

	downloadQuoteDocument(quoteId: string): Observable<FileResponse> {
		return this.documentsClient.generateQuoteDocument(quoteId);
	}

	sendAgentInvitationEmail$(request: AgentInvitationRequestModel) : Observable<boolean> {
		return this.emailClient.sendAgentInvitationEmail(request);
	}

	sendQuoteByEmail$(request: SendQuoteByEmailModel): Observable<boolean> {
		// AMBIENTE DE PRUEBAS: el PDF de cotización se genera y envía desde nuestro
		// propio servicio con la plantilla oficial. Si no hay datos de los 3 planes
		// (p. ej. pantalla de pago), se mantiene el envío original del CRM.
		if (!this.allPlans) {
			return this.emailClient.sendQuoteByEmail(request);
		}
		const body = {
			destinatarios: request.recipientEmails,
			quoteModel: request.quoteModel,
			planes: this.allPlans
		};
		return this.http.post<any>('/pdf-api/cotizacion-pdf/desde-cotizacion', body).pipe(
			map(r => !!(r && r.ok)),
			catchError(() => of(false))
		);
	}

	fromStepTwoTrue(){
		this.fromStepTwo = true
	}

	validateAgentCode(code: string) {
		return this.quoteClient.getValidateCode(code);
	}

	getCertificate$(quote: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.getCertificate(quote)
		.pipe(
			tap(r => {
			})
		);
	}

	updateCertificate$(quote: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.updateCertificate(quote)
		.pipe(
			tap(r => {
			})
		);
	}

	submitCertificate$(quote: QuoteModel): Observable<QuoteModel> {
		return this.quoteClient.submitCertificate(quote)
		.pipe(
			tap(r => {
			})
		);
	}

	/**
	 * Reset quoteModel to default empty state
	 */
	resetQuoteModel(): void {
		this.quoteModel = new GetQuoteResponseModel();
		this.quoteRateModel = new QuoteRateView();
		this.fromStepTwo = false;
		this.clearLastError();
	}
}
